import { DynamicPropertyEvent } from 'types/DynamicPropertyEvent';
import { DynamicPropertyType } from 'types/DynamicPropertyType';

import { parseDimensions } from '../parse';
import { makeCSVImportError } from '../tree';
import DynamicGroup from './Group';

export type DimensionsValue = {
  width: number;
  height: number;
};

function ifPositiveInteger(x: unknown): number | undefined {
  let asNumber: number;
  switch (typeof x) {
    case 'number':
      asNumber = x;
      break;
    case 'string':
      {
        if (!x) return undefined;
        asNumber = +x;
      }
      break;
    default:
      return undefined;
  }
  return isFinite(asNumber) && asNumber > 0 && Number.isSafeInteger(asNumber)
    ? asNumber
    : undefined;
}

export default class Dimensions extends DynamicGroup {
  type = DynamicPropertyType.DIMENSIONS;

  constructor(parent: DynamicGroup, name: string, value: DimensionsValue) {
    super(parent, name, value);
    // this.becomeObservable();
  }

  public getTuple(): [number, number] | undefined {
    const widthProp = this.getChild('width');
    const heightProp = this.getChild('height');
    if (!widthProp || !heightProp) {
      return undefined;
    }
    const widthValue = ifPositiveInteger(widthProp.value);
    const heightValue = ifPositiveInteger(heightProp.value);
    return widthValue == null || heightValue == null
      ? undefined
      : [widthValue, heightValue];
  }

  public setSize(width: number, height: number): void {
    this.getChild('width')?.set(width);
    this.getChild('height')?.set(height);
  }

  setDefault = () => {
    this.add('width', DynamicPropertyType.NUMBER, 300);
    this.add('height', DynamicPropertyType.NUMBER, 250);
  };

  getString = () => {
    const size = this.getTuple();
    if (!size) return '';
    const [width, height] = size;
    return `${width}x${height}`;
  };

  getPreviewData = () => {
    return this.getString();
  };

  setUsingCSV = (value: string) => {
    const axes = parseDimensions(value);
    if (!axes) {
      throw makeCSVImportError(this, 'Invalid dimensions value');
    }
    const [width, height] = axes;
    this.setSize(width, height);
    // The children may not pass their CHANGED up to this group (only ones
    // setState created do), so dispatch one here for both.
    this.dispatch(DynamicPropertyEvent.CHANGED);
  };

  getCSVData = () => {
    return this.getString();
  };
}
